import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { getStoryblokClientAccessToken } from "../../config/storyblok-access-token"
import storyBlokClient from "../../util/storyblokClient"
import { BlokReviewsType, ReviewsContextType } from "./types"

const ReviewsContext = createContext<ReviewsContextType | undefined>(undefined)
ReviewsContext.displayName = "ReviewsContext"

export const ReviewsContextProvider = ({
  preview = false,
  children,
  serverReviews,
}: PropsWithChildren<{
  preview?: boolean
  host?: Record<string, string>
  serverReviews?: BlokReviewsType | null
}>) => {
  const [blokReviews, setBlokReviews] = useState<BlokReviewsType | null>(
    serverReviews ?? null,
  )

  // No hardcoded fallback: the browser must only ever hold the public token
  // this environment was configured with. Most pages mount this provider
  // without `serverReviews`, so an environment missing the token renders the
  // reviews blocks empty rather than shipping a credential to every visitor.
  const token = getStoryblokClientAccessToken()

  useEffect(() => {
    if (blokReviews) return

    if (!token) return

    const fetchData = async () => {
      try {
        // storyBlokClient() already attaches the token as a request param;
        // interpolating it here too would send it twice.
        const { data } = await storyBlokClient().get(
          "datasource_entries?datasource=reviews-data",
        )

        const reviews = data.datasource_entries.reduce(
          (acc: Record<string, string>, curr: Record<string, string>) => {
            acc[curr.name.trim()] = curr.value
            return acc
          },
          {},
        )

        setBlokReviews(reviews)
      } catch (error) {
        console.error(`Fetch Reviews failed: ${error}`)
      }
    }

    fetchData()
  }, [blokReviews, token])
  const value = useMemo(
    () => ({
      blokReviews,
    }),
    [blokReviews],
  )

  return (
    <ReviewsContext.Provider value={value}>{children}</ReviewsContext.Provider>
  )
}

export const useReviewsContext = () => {
  const context = useContext(ReviewsContext)

  if (context === undefined) {
    throw new Error(
      "useReviewsContext must be used within a ReviewsContextProvider",
    )
  }

  return context
}

export default ReviewsContextProvider
